import type {
  AuthResponse,
  User,
  Session,
  StatsOverview,
  ChatMessage,
  AnalyzeResponse,
  Memory,
  TrainingProgressResponse,
  TrainingProgressStorePayload,
} from '../types/api';
import API_URL from '../config/PokerMindBrasil_ApiConfig_2026-03-29_v1.0';
import {
  buildTrainingProgressRequestBody,
  TRAINING_PROGRESS_ENDPOINT,
} from './trainingProgressContract';

const BASE_URL = API_URL;

function getToken(): string | null {
  try {
    const raw = localStorage.getItem('pm_auth');
    if (!raw) return null;
    return (JSON.parse(raw) as { token?: string }).token ?? null;
  } catch {
    return null;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isFormData = false
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!isFormData && body) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: isFormData
      ? (body as FormData)
      : body
      ? JSON.stringify(body)
      : undefined,
  });

  if (res.status === 401) {
    localStorage.removeItem('pm_auth');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err['error'] ?? err['message'] ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

async function requestOptional<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<T | null> {
  try {
    return await request<T>(method, path, body);
  } catch (error) {
    const message = error instanceof Error ? error.message.toLowerCase() : '';
    if (
      message.includes('404') ||
      message.includes('not found') ||
      message.includes('501') ||
      message.includes('405')
    ) {
      return null;
    }
    throw error;
  }
}

export const authApi = {
  register: (name: string, email: string, password: string) =>
    request<AuthResponse>('POST', '/auth/register', { name, email, password }),

  login: (email: string, password: string) =>
    request<AuthResponse>('POST', '/auth/login', { email, password }),

  me: () => request<{ user: User }>('GET', '/auth/me'),

  forgotPassword: (email: string) =>
    request<{ ok: boolean; error?: string }>('POST', '/auth/forgot-password', {
      email,
    }),

  resetPassword: (token: string, password: string) =>
    request<{ ok: boolean; error?: string }>('POST', '/auth/reset-password', {
      token,
      password,
    }),
};

export const onboardingApi = {
  get: () =>
    request<{ onboarding: { completed: 0 | 1 } }>('GET', '/onboarding'),

  save: (data: {
    game_type: string;
    stakes: string;
    experience: string;
    goal: string;
    frequency: string;
  }) => request<{ ok: boolean }>('POST', '/onboarding', data),
};

export const sessionsApi = {
  list: () => request<{ sessions: Session[] }>('GET', '/sessions'),

  get: (id: number) => request<Session>('GET', `/sessions/${id}`),

  delete: (id: number) =>
    request<{ ok: boolean }>('DELETE', `/sessions/${id}`),

  analyze: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('source', 'ggpoker');
    return request<AnalyzeResponse>('POST', '/analyze', form, true);
  },
};

export const statsApi = {
  overview: () => request<StatsOverview>('GET', '/stats/overview'),
};

export const chatApi = {
  send: (message: string, villain_profile?: string | null) =>
    request<{ response?: string; reply?: string; message?: string }>(
      'POST',
      '/chat',
      { message, mode: 'default', villain_profile: villain_profile ?? null }
    ),

  history: () => request<{ messages: ChatMessage[] }>('GET', '/chat'),

  clear: () => request<{ ok: boolean }>('DELETE', '/chat'),
};

export const memoryApi = {
  get: () => request<{ memory: Memory }>('GET', '/memory'),
};

export const trainingProgressApi = {
  get: () =>
    requestOptional<TrainingProgressResponse>('GET', TRAINING_PROGRESS_ENDPOINT),

  save: (progress: TrainingProgressStorePayload) =>
    requestOptional<{ ok: boolean; updatedAt?: string }>(
      'POST',
      TRAINING_PROGRESS_ENDPOINT,
      buildTrainingProgressRequestBody(progress)
    ),
};
